import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { Race } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputNullableDateTime,
    DateComp,
    InputKilometer,
    ValidatedInt,
    InputSelect,
    DataSourceLookup,
    DisplaySeparator,
    InputInt,
    InputBool,
    DisplayBool,
    InputNullableInt,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import { useCommonStore, useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class RaceGuiDef extends GuiDef<Race> {
    constructor() {
        super('Race');

        const Common = storeToRefs(useCommonStore());
        const DisciplinesForEventWithUseEventSource = computed(() => {
            return Common.DisciplinesForEventWithUseEventAsSelectLine.value;
        });
        const SportLevelsForEventWithUseEventSource = computed(() => {
            return Common.SportLevelsForEventWithUseEventAsSelectLine.value;
        });
        const EventCommon = storeToRefs(useEventCommonStore());
        const RacesWithNoneSource = computed(() => {
            return EventCommon.RacesWithNoneAsSelectLine.value;
        });
        const DecimalPlacesInResultsWithUseEventSource = computed(() => {
            return Common.DecimalPlacesInResultsWithUseEventAsSelectLine.value;
        });
        const RankingResolutionsWithUseEventSource = computed(() => {
            return Common.RankingResolutionsWithUseEventAsSelectLine.value;
        });
        const RoundTypesWithUseEventSource = computed(() => {
            return Common.RoundTypesWithUseEventAsSelectLine.value;
        });

        const Name = new FieldDef<Race>();
        Name.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'race.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (race) => race.Name;
        Name.setValue = (race, value) => (race.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = null;
        Name.SortDirection = undefined;
        Name.cellClassRules = undefined;
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const ShortName = new FieldDef<Race>();
        ShortName.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        ShortName.Name = 'ShortName';
        ShortName.FieldName = 'ShortName';
        ShortName.DefinedFor = 'Both';
        ShortName.TranslationKey = 'race.shortname';
        ShortName.Editable = true;
        ShortName.Filterable = true;
        ShortName.Sortable = true;
        ShortName.CssClasses = '';
        ShortName.value = (race) => race.ShortName;
        ShortName.setValue = (race, value) => (race.ShortName = value);
        ShortName.DetailEditor = markRaw(InputText);
        ShortName.DetailEditorProps = {};
        ShortName.CellEditor = markRaw(InputText);
        ShortName.CellEditorProps = {};
        ShortName.CellRenderer = markRaw(ValidatedText);
        ShortName.CellRendererProps = {};
        ShortName.Validator = null;
        ShortName.SortDirection = undefined;
        ShortName.cellClassRules = undefined;
        ShortName.MinWidth = undefined;
        ShortName.MaxWidth = undefined;
        ShortName.Tooltip = null;
        this.Fields.push(ShortName);

        const Date = new FieldDef<Race>();
        Date.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        Date.Name = 'Date';
        Date.FieldName = 'Date';
        Date.DefinedFor = 'Both';
        Date.TranslationKey = 'race.date';
        Date.Editable = true;
        Date.Filterable = true;
        Date.Sortable = true;
        Date.CssClasses = '';
        Date.value = (race) => race.Date;
        Date.setValue = (race, value) => (race.Date = value);
        Date.DetailEditor = markRaw(InputNullableDateTime);
        Date.DetailEditorProps = { format: 'DATETIME' as DateType };
        Date.CellEditor = markRaw(InputNullableDateTime);
        Date.CellEditorProps = { format: 'DATETIME' as DateType };
        Date.CellRenderer = markRaw(DateComp);
        Date.CellRendererProps = { format: 'DATETIME' as DateType };
        Date.Validator = null;
        Date.SortDirection = undefined;
        Date.cellClassRules = undefined;
        Date.MinWidth = 180;
        Date.MaxWidth = undefined;
        Date.Tooltip = null;
        this.Fields.push(Date);

        const Distance = new FieldDef<Race>();
        Distance.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        Distance.Name = 'Distance';
        Distance.FieldName = 'Distance';
        Distance.DefinedFor = 'Both';
        Distance.TranslationKey = 'race.distance';
        Distance.Editable = true;
        Distance.Filterable = false;
        Distance.Sortable = true;
        Distance.CssClasses = '';
        Distance.value = (race) => race.Distance;
        Distance.setValue = (race, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            race.Distance = value;
        };
        Distance.DetailEditor = markRaw(InputKilometer);
        Distance.DetailEditorProps = {};
        Distance.CellEditor = markRaw(InputKilometer);
        Distance.CellEditorProps = {};
        Distance.CellRenderer = markRaw(ValidatedInt);
        Distance.CellRendererProps = {};
        Distance.Validator = null;
        Distance.SortDirection = undefined;
        Distance.cellClassRules = undefined;
        Distance.MinWidth = undefined;
        Distance.MaxWidth = undefined;
        Distance.Tooltip = null;
        this.Fields.push(Distance);

        const DisciplineID = new FieldDef<Race>();
        DisciplineID.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        DisciplineID.Name = 'DisciplineID';
        DisciplineID.FieldName = 'DisciplineID';
        DisciplineID.DefinedFor = 'Both';
        DisciplineID.TranslationKey = 'race.disciplineid';
        DisciplineID.Editable = true;
        DisciplineID.Filterable = true;
        DisciplineID.Sortable = true;
        DisciplineID.CssClasses = '';
        DisciplineID.value = (race) => race.DisciplineID;
        DisciplineID.setValue = (race, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            race.DisciplineID = value;
        };
        DisciplineID.DetailEditor = markRaw(InputSelect);
        DisciplineID.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: DisciplinesForEventWithUseEventSource } };
        DisciplineID.CellEditor = markRaw(InputSelect);
        DisciplineID.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: DisciplinesForEventWithUseEventSource },
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        DisciplineID.CellRenderer = markRaw(DataSourceLookup);
        DisciplineID.CellRendererProps = { datasource: { mapsource: DisciplinesForEventWithUseEventSource } };
        DisciplineID.ExportValueFormatter = (obj: Race) => {
            return DisciplinesForEventWithUseEventSource.value.get(obj.DisciplineID)?.text;
        };
        DisciplineID.Validator = null;
        DisciplineID.SortDirection = undefined;
        DisciplineID.cellClassRules = undefined;
        DisciplineID.MinWidth = undefined;
        DisciplineID.MaxWidth = undefined;
        DisciplineID.Tooltip = null;
        this.Fields.push(DisciplineID);

        const SportsLevelID = new FieldDef<Race>();
        SportsLevelID.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        SportsLevelID.Name = 'SportsLevelID';
        SportsLevelID.FieldName = 'SportsLevelID';
        SportsLevelID.DefinedFor = 'Both';
        SportsLevelID.TranslationKey = 'race.sportslevelid';
        SportsLevelID.Editable = true;
        SportsLevelID.Filterable = true;
        SportsLevelID.Sortable = true;
        SportsLevelID.CssClasses = '';
        SportsLevelID.value = (race) => race.SportsLevelID;
        SportsLevelID.setValue = (race, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            race.SportsLevelID = value;
        };
        SportsLevelID.DetailEditor = markRaw(InputSelect);
        SportsLevelID.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: SportLevelsForEventWithUseEventSource } };
        SportsLevelID.CellEditor = markRaw(InputSelect);
        SportsLevelID.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: SportLevelsForEventWithUseEventSource },
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        SportsLevelID.CellRenderer = markRaw(DataSourceLookup);
        SportsLevelID.CellRendererProps = { datasource: { mapsource: SportLevelsForEventWithUseEventSource } };
        SportsLevelID.ExportValueFormatter = (obj: Race) => {
            return SportLevelsForEventWithUseEventSource.value.get(obj.SportsLevelID)?.text;
        };
        SportsLevelID.Validator = null;
        SportsLevelID.SortDirection = undefined;
        SportsLevelID.cellClassRules = undefined;
        SportsLevelID.MinWidth = undefined;
        SportsLevelID.MaxWidth = undefined;
        SportsLevelID.Tooltip = null;
        this.Fields.push(SportsLevelID);

        const separator0 = new FieldDef<Race>();
        separator0.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        separator0.Name = 'separator0';
        separator0.FieldName = 'separator0';
        separator0.DefinedFor = 'Detail';
        separator0.TranslationKey = 'race.sort';
        separator0.Editable = true;
        separator0.Filterable = false;
        separator0.Sortable = true;
        separator0.CssClasses = '';
        separator0.value = () => {
            return 'race.sort';
        };
        separator0.setValue = () => {};
        separator0.DetailEditor = markRaw(DisplaySeparator);
        separator0.DetailEditorProps = {};
        separator0.CellEditor = markRaw(DisplaySeparator);
        separator0.CellEditorProps = {};
        separator0.CellRenderer = markRaw(DisplaySeparator);
        separator0.CellRendererProps = {};
        separator0.Validator = null;
        separator0.SortDirection = undefined;
        separator0.cellClassRules = undefined;
        separator0.MinWidth = undefined;
        separator0.MaxWidth = undefined;
        separator0.Tooltip = null;
        this.Fields.push(separator0);

        const Number = new FieldDef<Race>();
        Number.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        Number.Name = 'Number';
        Number.FieldName = 'Number';
        Number.DefinedFor = 'Both';
        Number.TranslationKey = 'race.number';
        Number.Editable = true;
        Number.Filterable = false;
        Number.Sortable = true;
        Number.CssClasses = '';
        Number.value = (race) => race.Number;
        Number.setValue = (race, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            race.Number = value;
        };
        Number.DetailEditor = markRaw(InputInt);
        Number.DetailEditorProps = {};
        Number.CellEditor = markRaw(InputInt);
        Number.CellEditorProps = {};
        Number.CellRenderer = markRaw(ValidatedInt);
        Number.CellRendererProps = {};
        Number.Validator = null;
        Number.SortDirection = undefined;
        Number.cellClassRules = undefined;
        Number.MinWidth = undefined;
        Number.MaxWidth = undefined;
        Number.Tooltip = null;
        this.Fields.push(Number);

        const Level = new FieldDef<Race>();
        Level.Group = new GroupDef('race.level', 'race.run', false);
        Level.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        Level.Name = 'Level';
        Level.FieldName = 'Level';
        Level.DefinedFor = 'Both';
        Level.TranslationKey = 'race.level';
        Level.Editable = true;
        Level.Filterable = false;
        Level.Sortable = true;
        Level.CssClasses = '';
        Level.value = (race) => race.Level;
        Level.setValue = (race, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            race.Level = value;
        };
        Level.DetailEditor = markRaw(InputInt);
        Level.DetailEditorProps = {};
        Level.CellEditor = markRaw(InputInt);
        Level.CellEditorProps = {};
        Level.CellRenderer = markRaw(ValidatedInt);
        Level.CellRendererProps = {};
        Level.Validator = null;
        Level.SortDirection = undefined;
        Level.cellClassRules = undefined;
        Level.MinWidth = undefined;
        Level.MaxWidth = undefined;
        Level.Tooltip = null;
        this.Fields.push(Level);

        const Run = new FieldDef<Race>();
        Run.Group = new GroupDef('race.level', 'race.run', false);
        Run.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        Run.Name = 'Run';
        Run.FieldName = 'Run';
        Run.DefinedFor = 'Both';
        Run.TranslationKey = 'race.run';
        Run.Editable = true;
        Run.Filterable = false;
        Run.Sortable = true;
        Run.CssClasses = '';
        Run.value = (race) => race.Run;
        Run.setValue = (race, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            race.Run = value;
        };
        Run.DetailEditor = markRaw(InputInt);
        Run.DetailEditorProps = {};
        Run.CellEditor = markRaw(InputInt);
        Run.CellEditorProps = {};
        Run.CellRenderer = markRaw(ValidatedInt);
        Run.CellRendererProps = {};
        Run.Validator = null;
        Run.SortDirection = undefined;
        Run.cellClassRules = undefined;
        Run.MinWidth = undefined;
        Run.MaxWidth = undefined;
        Run.Tooltip = null;
        this.Fields.push(Run);

        const ResultParent = new FieldDef<Race>();
        ResultParent.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        ResultParent.Name = 'ResultParent';
        ResultParent.FieldName = 'ResultParent';
        ResultParent.DefinedFor = 'Both';
        ResultParent.TranslationKey = 'race.resultparent';
        ResultParent.Editable = true;
        ResultParent.Filterable = false;
        ResultParent.Sortable = true;
        ResultParent.CssClasses = '';
        ResultParent.value = (race) => race.ResultParent;
        ResultParent.setValue = (race, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            race.ResultParent = value;
        };
        ResultParent.DetailEditor = markRaw(InputSelect);
        ResultParent.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: RacesWithNoneSource } };
        ResultParent.CellEditor = markRaw(InputSelect);
        ResultParent.CellEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: RacesWithNoneSource }, autoOpen: true };
        // @ts-expect-error @ts-ignore
        ResultParent.CellRenderer = markRaw(DataSourceLookup);
        ResultParent.CellRendererProps = { datasource: { mapsource: RacesWithNoneSource } };
        ResultParent.ExportValueFormatter = (obj: Race) => {
            return RacesWithNoneSource.value.get(obj.ResultParent)?.text;
        };
        ResultParent.Validator = null;
        ResultParent.SortDirection = undefined;
        ResultParent.cellClassRules = undefined;
        ResultParent.MinWidth = undefined;
        ResultParent.MaxWidth = undefined;
        ResultParent.Tooltip = null;
        this.Fields.push(ResultParent);

        const separator1 = new FieldDef<Race>();
        separator1.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        separator1.Name = 'separator1';
        separator1.FieldName = 'separator1';
        separator1.DefinedFor = 'Detail';
        separator1.TranslationKey = 'race.timing';
        separator1.Editable = true;
        separator1.Filterable = false;
        separator1.Sortable = true;
        separator1.CssClasses = '';
        separator1.value = () => {
            return 'race.timing';
        };
        separator1.setValue = () => {};
        separator1.DetailEditor = markRaw(DisplaySeparator);
        separator1.DetailEditorProps = {};
        separator1.CellEditor = markRaw(DisplaySeparator);
        separator1.CellEditorProps = {};
        separator1.CellRenderer = markRaw(DisplaySeparator);
        separator1.CellRendererProps = {};
        separator1.Validator = null;
        separator1.SortDirection = undefined;
        separator1.cellClassRules = undefined;
        separator1.MinWidth = undefined;
        separator1.MaxWidth = undefined;
        separator1.Tooltip = null;
        this.Fields.push(separator1);

        const Pursuit = new FieldDef<Race>();
        Pursuit.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        Pursuit.Name = 'Pursuit';
        Pursuit.FieldName = 'Pursuit';
        Pursuit.DefinedFor = 'Both';
        Pursuit.TranslationKey = 'race.pursuit';
        Pursuit.Editable = true;
        Pursuit.Filterable = false;
        Pursuit.Sortable = true;
        Pursuit.CssClasses = '';
        Pursuit.value = (race) => race.Pursuit;
        Pursuit.setValue = (race, value) => (race.Pursuit = value);
        Pursuit.DetailEditor = markRaw(InputBool);
        Pursuit.DetailEditorProps = { label: 'race.pursuit' };
        Pursuit.CellEditor = markRaw(InputBool);
        Pursuit.CellEditorProps = {};
        Pursuit.CellRenderer = markRaw(DisplayBool);
        Pursuit.CellRendererProps = {};
        Pursuit.Validator = null;
        Pursuit.SortDirection = undefined;
        Pursuit.cellClassRules = undefined;
        Pursuit.MinWidth = undefined;
        Pursuit.MaxWidth = undefined;
        Pursuit.Tooltip = null;
        this.Fields.push(Pursuit);

        const IndividualStart = new FieldDef<Race>();
        IndividualStart.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        IndividualStart.Name = 'IndividualStart';
        IndividualStart.FieldName = 'IndividualStart';
        IndividualStart.DefinedFor = 'Both';
        IndividualStart.TranslationKey = 'race.individualstart';
        IndividualStart.Editable = true;
        IndividualStart.Filterable = false;
        IndividualStart.Sortable = true;
        IndividualStart.CssClasses = '';
        IndividualStart.value = (race) => race.IndividualStart;
        IndividualStart.setValue = (race, value) => (race.IndividualStart = value);
        IndividualStart.DetailEditor = markRaw(InputBool);
        IndividualStart.DetailEditorProps = { label: 'race.individualstart' };
        IndividualStart.CellEditor = markRaw(InputBool);
        IndividualStart.CellEditorProps = {};
        IndividualStart.CellRenderer = markRaw(DisplayBool);
        IndividualStart.CellRendererProps = {};
        IndividualStart.Validator = null;
        IndividualStart.SortDirection = undefined;
        IndividualStart.cellClassRules = undefined;
        IndividualStart.MinWidth = undefined;
        IndividualStart.MaxWidth = undefined;
        IndividualStart.Tooltip = null;
        this.Fields.push(IndividualStart);

        const separator2 = new FieldDef<Race>();
        separator2.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        separator2.Name = 'separator2';
        separator2.FieldName = 'separator2';
        separator2.DefinedFor = 'Detail';
        separator2.TranslationKey = 'race.signup';
        separator2.Editable = true;
        separator2.Filterable = false;
        separator2.Sortable = true;
        separator2.CssClasses = '';
        separator2.value = () => {
            return 'race.signup';
        };
        separator2.setValue = () => {};
        separator2.DetailEditor = markRaw(DisplaySeparator);
        separator2.DetailEditorProps = {};
        separator2.CellEditor = markRaw(DisplaySeparator);
        separator2.CellEditorProps = {};
        separator2.CellRenderer = markRaw(DisplaySeparator);
        separator2.CellRendererProps = {};
        separator2.Validator = null;
        separator2.SortDirection = undefined;
        separator2.cellClassRules = undefined;
        separator2.MinWidth = undefined;
        separator2.MaxWidth = undefined;
        separator2.Tooltip = null;
        this.Fields.push(separator2);

        const RequireActiveLicense = new FieldDef<Race>();
        RequireActiveLicense.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        RequireActiveLicense.Name = 'RequireActiveLicense';
        RequireActiveLicense.FieldName = 'RequireActiveLicense';
        RequireActiveLicense.DefinedFor = 'Both';
        RequireActiveLicense.TranslationKey = 'race.requireactivelicense';
        RequireActiveLicense.Editable = true;
        RequireActiveLicense.Filterable = false;
        RequireActiveLicense.Sortable = true;
        RequireActiveLicense.CssClasses = '';
        RequireActiveLicense.value = (race) => race.RequireActiveLicense;
        RequireActiveLicense.setValue = (race, value) => (race.RequireActiveLicense = value);
        RequireActiveLicense.DetailEditor = markRaw(InputBool);
        RequireActiveLicense.DetailEditorProps = { label: 'race.requireactivelicense' };
        RequireActiveLicense.CellEditor = markRaw(InputBool);
        RequireActiveLicense.CellEditorProps = {};
        RequireActiveLicense.CellRenderer = markRaw(DisplayBool);
        RequireActiveLicense.CellRendererProps = {};
        RequireActiveLicense.Validator = null;
        RequireActiveLicense.SortDirection = undefined;
        RequireActiveLicense.cellClassRules = undefined;
        RequireActiveLicense.MinWidth = undefined;
        RequireActiveLicense.MaxWidth = undefined;
        RequireActiveLicense.Tooltip = null;
        this.Fields.push(RequireActiveLicense);

        const CancellationDeadline = new FieldDef<Race>();
        CancellationDeadline.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        CancellationDeadline.Name = 'CancellationDeadline';
        CancellationDeadline.FieldName = 'CancellationDeadline';
        CancellationDeadline.DefinedFor = 'Both';
        CancellationDeadline.TranslationKey = 'race.cancellationdeadline';
        CancellationDeadline.Editable = true;
        CancellationDeadline.Filterable = true;
        CancellationDeadline.Sortable = true;
        CancellationDeadline.CssClasses = '';
        CancellationDeadline.value = (race) => race.CancellationDeadline;
        CancellationDeadline.setValue = (race, value) => (race.CancellationDeadline = value);
        CancellationDeadline.DetailEditor = markRaw(InputNullableDateTime);
        CancellationDeadline.DetailEditorProps = { format: 'DATETIME' as DateType };
        CancellationDeadline.CellEditor = markRaw(InputNullableDateTime);
        CancellationDeadline.CellEditorProps = { format: 'DATETIME' as DateType };
        CancellationDeadline.CellRenderer = markRaw(DateComp);
        CancellationDeadline.CellRendererProps = { format: 'DATETIME' as DateType };
        CancellationDeadline.Validator = null;
        CancellationDeadline.SortDirection = undefined;
        CancellationDeadline.cellClassRules = undefined;
        CancellationDeadline.MinWidth = 180;
        CancellationDeadline.MaxWidth = undefined;
        CancellationDeadline.Tooltip = null;
        this.Fields.push(CancellationDeadline);

        const MaxContestants = new FieldDef<Race>();
        MaxContestants.Tab = new TabDefinition('race', 'race.race', 'fa-flag-checkered');
        MaxContestants.Name = 'MaxContestants';
        MaxContestants.FieldName = 'MaxContestants';
        MaxContestants.DefinedFor = 'Both';
        MaxContestants.TranslationKey = 'race.maxcontestants';
        MaxContestants.Editable = true;
        MaxContestants.Filterable = false;
        MaxContestants.Sortable = true;
        MaxContestants.CssClasses = '';
        MaxContestants.value = (race) => race.MaxContestants;
        MaxContestants.setValue = (race, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            race.MaxContestants = value;
        };
        MaxContestants.DetailEditor = markRaw(InputNullableInt);
        MaxContestants.DetailEditorProps = {};
        MaxContestants.CellEditor = markRaw(InputNullableInt);
        MaxContestants.CellEditorProps = {};
        MaxContestants.CellRenderer = markRaw(ValidatedInt);
        MaxContestants.CellRendererProps = {};
        MaxContestants.Validator = null;
        MaxContestants.SortDirection = undefined;
        MaxContestants.cellClassRules = undefined;
        MaxContestants.MinWidth = undefined;
        MaxContestants.MaxWidth = undefined;
        MaxContestants.Tooltip = null;
        this.Fields.push(MaxContestants);

        const RelayTeamMinContestants = new FieldDef<Race>();
        RelayTeamMinContestants.Group = new GroupDef('race.relayamount', '', true);
        RelayTeamMinContestants.Tab = new TabDefinition('relay', 'race.relay', 'fa-users');
        RelayTeamMinContestants.Name = 'RelayTeamMinContestants';
        RelayTeamMinContestants.FieldName = 'RelayTeamMinContestants';
        RelayTeamMinContestants.DefinedFor = 'Detail';
        RelayTeamMinContestants.TranslationKey = 'race.relayteammincontestants';
        RelayTeamMinContestants.Editable = true;
        RelayTeamMinContestants.Filterable = false;
        RelayTeamMinContestants.Sortable = true;
        RelayTeamMinContestants.CssClasses = '';
        RelayTeamMinContestants.value = (race) => race.RelayTeamMinContestants;
        RelayTeamMinContestants.setValue = (race, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            race.RelayTeamMinContestants = value;
        };
        RelayTeamMinContestants.DetailEditor = markRaw(InputNullableInt);
        RelayTeamMinContestants.DetailEditorProps = {};
        RelayTeamMinContestants.CellEditor = markRaw(InputNullableInt);
        RelayTeamMinContestants.CellEditorProps = {};
        RelayTeamMinContestants.CellRenderer = markRaw(ValidatedInt);
        RelayTeamMinContestants.CellRendererProps = {};
        RelayTeamMinContestants.Validator = null;
        RelayTeamMinContestants.SortDirection = undefined;
        RelayTeamMinContestants.cellClassRules = undefined;
        RelayTeamMinContestants.MinWidth = undefined;
        RelayTeamMinContestants.MaxWidth = undefined;
        RelayTeamMinContestants.Tooltip = null;
        this.Fields.push(RelayTeamMinContestants);

        const RelayTeamMaxContestants = new FieldDef<Race>();
        RelayTeamMaxContestants.Group = new GroupDef('race.relayamount', '', true);
        RelayTeamMaxContestants.Tab = new TabDefinition('relay', 'race.relay', 'fa-users');
        RelayTeamMaxContestants.Name = 'RelayTeamMaxContestants';
        RelayTeamMaxContestants.FieldName = 'RelayTeamMaxContestants';
        RelayTeamMaxContestants.DefinedFor = 'Detail';
        RelayTeamMaxContestants.TranslationKey = 'race.relayteammaxcontestants';
        RelayTeamMaxContestants.Editable = true;
        RelayTeamMaxContestants.Filterable = false;
        RelayTeamMaxContestants.Sortable = true;
        RelayTeamMaxContestants.CssClasses = '';
        RelayTeamMaxContestants.value = (race) => race.RelayTeamMaxContestants;
        RelayTeamMaxContestants.setValue = (race, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            race.RelayTeamMaxContestants = value;
        };
        RelayTeamMaxContestants.DetailEditor = markRaw(InputNullableInt);
        RelayTeamMaxContestants.DetailEditorProps = {};
        RelayTeamMaxContestants.CellEditor = markRaw(InputNullableInt);
        RelayTeamMaxContestants.CellEditorProps = {};
        RelayTeamMaxContestants.CellRenderer = markRaw(ValidatedInt);
        RelayTeamMaxContestants.CellRendererProps = {};
        RelayTeamMaxContestants.Validator = null;
        RelayTeamMaxContestants.SortDirection = undefined;
        RelayTeamMaxContestants.cellClassRules = undefined;
        RelayTeamMaxContestants.MinWidth = undefined;
        RelayTeamMaxContestants.MaxWidth = undefined;
        RelayTeamMaxContestants.Tooltip = null;
        this.Fields.push(RelayTeamMaxContestants);

        const RelayAllowTeamnameChange = new FieldDef<Race>();
        RelayAllowTeamnameChange.Tab = new TabDefinition('relay', 'race.relay', 'fa-users');
        RelayAllowTeamnameChange.Name = 'RelayAllowTeamnameChange';
        RelayAllowTeamnameChange.FieldName = 'RelayAllowTeamnameChange';
        RelayAllowTeamnameChange.DefinedFor = 'Both';
        RelayAllowTeamnameChange.TranslationKey = 'race.relayallowteamnamechange';
        RelayAllowTeamnameChange.Editable = true;
        RelayAllowTeamnameChange.Filterable = false;
        RelayAllowTeamnameChange.Sortable = true;
        RelayAllowTeamnameChange.CssClasses = '';
        RelayAllowTeamnameChange.value = (race) => race.RelayAllowTeamnameChange;
        RelayAllowTeamnameChange.setValue = (race, value) => (race.RelayAllowTeamnameChange = value);
        RelayAllowTeamnameChange.DetailEditor = markRaw(InputBool);
        RelayAllowTeamnameChange.DetailEditorProps = { label: 'race.relayallowteamnamechange' };
        RelayAllowTeamnameChange.CellEditor = markRaw(InputBool);
        RelayAllowTeamnameChange.CellEditorProps = {};
        RelayAllowTeamnameChange.CellRenderer = markRaw(DisplayBool);
        RelayAllowTeamnameChange.CellRendererProps = {};
        RelayAllowTeamnameChange.Validator = null;
        RelayAllowTeamnameChange.SortDirection = undefined;
        RelayAllowTeamnameChange.cellClassRules = undefined;
        RelayAllowTeamnameChange.MinWidth = undefined;
        RelayAllowTeamnameChange.MaxWidth = undefined;
        RelayAllowTeamnameChange.Tooltip = 'race.relayallowteamnamechangetooltip';
        this.Fields.push(RelayAllowTeamnameChange);

        const RelayDuplicateParticipant = new FieldDef<Race>();
        RelayDuplicateParticipant.Tab = new TabDefinition('relay', 'race.relay', 'fa-users');
        RelayDuplicateParticipant.Name = 'RelayDuplicateParticipant';
        RelayDuplicateParticipant.FieldName = 'RelayDuplicateParticipant';
        RelayDuplicateParticipant.DefinedFor = 'Both';
        RelayDuplicateParticipant.TranslationKey = 'race.relayduplicateparticipant';
        RelayDuplicateParticipant.Editable = true;
        RelayDuplicateParticipant.Filterable = false;
        RelayDuplicateParticipant.Sortable = true;
        RelayDuplicateParticipant.CssClasses = '';
        RelayDuplicateParticipant.value = (race) => race.RelayDuplicateParticipant;
        RelayDuplicateParticipant.setValue = (race, value) => (race.RelayDuplicateParticipant = value);
        RelayDuplicateParticipant.DetailEditor = markRaw(InputBool);
        RelayDuplicateParticipant.DetailEditorProps = { label: 'race.relayduplicateparticipant' };
        RelayDuplicateParticipant.CellEditor = markRaw(InputBool);
        RelayDuplicateParticipant.CellEditorProps = {};
        RelayDuplicateParticipant.CellRenderer = markRaw(DisplayBool);
        RelayDuplicateParticipant.CellRendererProps = {};
        RelayDuplicateParticipant.Validator = null;
        RelayDuplicateParticipant.SortDirection = undefined;
        RelayDuplicateParticipant.cellClassRules = undefined;
        RelayDuplicateParticipant.MinWidth = undefined;
        RelayDuplicateParticipant.MaxWidth = undefined;
        RelayDuplicateParticipant.Tooltip = 'race.relayduplicateparticipanttooltip';
        this.Fields.push(RelayDuplicateParticipant);

        const RelayHideChip = new FieldDef<Race>();
        RelayHideChip.Tab = new TabDefinition('relay', 'race.relay', 'fa-users');
        RelayHideChip.Name = 'RelayHideChip';
        RelayHideChip.FieldName = 'RelayHideChip';
        RelayHideChip.DefinedFor = 'Both';
        RelayHideChip.TranslationKey = 'race.relayhidechip';
        RelayHideChip.Editable = true;
        RelayHideChip.Filterable = false;
        RelayHideChip.Sortable = true;
        RelayHideChip.CssClasses = '';
        RelayHideChip.value = (race) => race.RelayHideChip;
        RelayHideChip.setValue = (race, value) => (race.RelayHideChip = value);
        RelayHideChip.DetailEditor = markRaw(InputBool);
        RelayHideChip.DetailEditorProps = { label: 'race.relayhidechip' };
        RelayHideChip.CellEditor = markRaw(InputBool);
        RelayHideChip.CellEditorProps = {};
        RelayHideChip.CellRenderer = markRaw(DisplayBool);
        RelayHideChip.CellRendererProps = {};
        RelayHideChip.Validator = null;
        RelayHideChip.SortDirection = undefined;
        RelayHideChip.cellClassRules = undefined;
        RelayHideChip.MinWidth = undefined;
        RelayHideChip.MaxWidth = undefined;
        RelayHideChip.Tooltip = 'race.relayhidechiptooltip';
        this.Fields.push(RelayHideChip);

        const RelayHideSearch = new FieldDef<Race>();
        RelayHideSearch.Tab = new TabDefinition('relay', 'race.relay', 'fa-users');
        RelayHideSearch.Name = 'RelayHideSearch';
        RelayHideSearch.FieldName = 'RelayHideSearch';
        RelayHideSearch.DefinedFor = 'Both';
        RelayHideSearch.TranslationKey = 'race.relayhidesearch';
        RelayHideSearch.Editable = true;
        RelayHideSearch.Filterable = false;
        RelayHideSearch.Sortable = true;
        RelayHideSearch.CssClasses = '';
        RelayHideSearch.value = (race) => race.RelayHideSearch;
        RelayHideSearch.setValue = (race, value) => (race.RelayHideSearch = value);
        RelayHideSearch.DetailEditor = markRaw(InputBool);
        RelayHideSearch.DetailEditorProps = { label: 'race.relayhidesearch' };
        RelayHideSearch.CellEditor = markRaw(InputBool);
        RelayHideSearch.CellEditorProps = {};
        RelayHideSearch.CellRenderer = markRaw(DisplayBool);
        RelayHideSearch.CellRendererProps = {};
        RelayHideSearch.Validator = null;
        RelayHideSearch.SortDirection = undefined;
        RelayHideSearch.cellClassRules = undefined;
        RelayHideSearch.MinWidth = undefined;
        RelayHideSearch.MaxWidth = undefined;
        RelayHideSearch.Tooltip = 'race.relayhidesearchtooltip';
        this.Fields.push(RelayHideSearch);

        const RelayGetParticipantInfo = new FieldDef<Race>();
        RelayGetParticipantInfo.Tab = new TabDefinition('relay', 'race.relay', 'fa-users');
        RelayGetParticipantInfo.Name = 'RelayGetParticipantInfo';
        RelayGetParticipantInfo.FieldName = 'RelayGetParticipantInfo';
        RelayGetParticipantInfo.DefinedFor = 'Both';
        RelayGetParticipantInfo.TranslationKey = 'race.relaygetparticipantinfo';
        RelayGetParticipantInfo.Editable = true;
        RelayGetParticipantInfo.Filterable = false;
        RelayGetParticipantInfo.Sortable = true;
        RelayGetParticipantInfo.CssClasses = '';
        RelayGetParticipantInfo.value = (race) => race.RelayGetParticipantInfo;
        RelayGetParticipantInfo.setValue = (race, value) => (race.RelayGetParticipantInfo = value);
        RelayGetParticipantInfo.DetailEditor = markRaw(InputBool);
        RelayGetParticipantInfo.DetailEditorProps = { label: 'race.relaygetparticipantinfo' };
        RelayGetParticipantInfo.CellEditor = markRaw(InputBool);
        RelayGetParticipantInfo.CellEditorProps = {};
        RelayGetParticipantInfo.CellRenderer = markRaw(DisplayBool);
        RelayGetParticipantInfo.CellRendererProps = {};
        RelayGetParticipantInfo.Validator = null;
        RelayGetParticipantInfo.SortDirection = undefined;
        RelayGetParticipantInfo.cellClassRules = undefined;
        RelayGetParticipantInfo.MinWidth = undefined;
        RelayGetParticipantInfo.MaxWidth = undefined;
        RelayGetParticipantInfo.Tooltip = 'race.relaygetparticipantinfotooltip';
        this.Fields.push(RelayGetParticipantInfo);

        const DecimalPlacesInResult = new FieldDef<Race>();
        DecimalPlacesInResult.Tab = new TabDefinition('result', 'race.results', 'fa-ranking-star');
        DecimalPlacesInResult.Name = 'DecimalPlacesInResult';
        DecimalPlacesInResult.FieldName = 'DecimalPlacesInResult';
        DecimalPlacesInResult.DefinedFor = 'Both';
        DecimalPlacesInResult.TranslationKey = 'race.decimalplacesinresult';
        DecimalPlacesInResult.Editable = true;
        DecimalPlacesInResult.Filterable = true;
        DecimalPlacesInResult.Sortable = true;
        DecimalPlacesInResult.CssClasses = '';
        DecimalPlacesInResult.value = (race) => race.DecimalPlacesInResult;
        DecimalPlacesInResult.setValue = (race, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            race.DecimalPlacesInResult = value;
        };
        DecimalPlacesInResult.DetailEditor = markRaw(InputSelect);
        DecimalPlacesInResult.DetailEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: DecimalPlacesInResultsWithUseEventSource },
        };
        DecimalPlacesInResult.CellEditor = markRaw(InputSelect);
        DecimalPlacesInResult.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: DecimalPlacesInResultsWithUseEventSource },
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        DecimalPlacesInResult.CellRenderer = markRaw(DataSourceLookup);
        DecimalPlacesInResult.CellRendererProps = { datasource: { mapsource: DecimalPlacesInResultsWithUseEventSource } };
        DecimalPlacesInResult.ExportValueFormatter = (obj: Race) => {
            return DecimalPlacesInResultsWithUseEventSource.value.get(obj.DecimalPlacesInResult)?.text;
        };
        DecimalPlacesInResult.Validator = null;
        DecimalPlacesInResult.SortDirection = undefined;
        DecimalPlacesInResult.cellClassRules = undefined;
        DecimalPlacesInResult.MinWidth = undefined;
        DecimalPlacesInResult.MaxWidth = undefined;
        DecimalPlacesInResult.Tooltip = null;
        this.Fields.push(DecimalPlacesInResult);

        const RankingResolution = new FieldDef<Race>();
        RankingResolution.Tab = new TabDefinition('result', 'race.results', 'fa-ranking-star');
        RankingResolution.Name = 'RankingResolution';
        RankingResolution.FieldName = 'RankingResolution';
        RankingResolution.DefinedFor = 'Both';
        RankingResolution.TranslationKey = 'race.rankingresolution';
        RankingResolution.Editable = true;
        RankingResolution.Filterable = true;
        RankingResolution.Sortable = true;
        RankingResolution.CssClasses = '';
        RankingResolution.value = (race) => race.RankingResolution;
        RankingResolution.setValue = (race, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            race.RankingResolution = value;
        };
        RankingResolution.DetailEditor = markRaw(InputSelect);
        RankingResolution.DetailEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: RankingResolutionsWithUseEventSource },
        };
        RankingResolution.CellEditor = markRaw(InputSelect);
        RankingResolution.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: RankingResolutionsWithUseEventSource },
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        RankingResolution.CellRenderer = markRaw(DataSourceLookup);
        RankingResolution.CellRendererProps = { datasource: { mapsource: RankingResolutionsWithUseEventSource } };
        RankingResolution.ExportValueFormatter = (obj: Race) => {
            return RankingResolutionsWithUseEventSource.value.get(obj.RankingResolution)?.text;
        };
        RankingResolution.Validator = null;
        RankingResolution.SortDirection = undefined;
        RankingResolution.cellClassRules = undefined;
        RankingResolution.MinWidth = undefined;
        RankingResolution.MaxWidth = undefined;
        RankingResolution.Tooltip = null;
        this.Fields.push(RankingResolution);

        const RoundType = new FieldDef<Race>();
        RoundType.Tab = new TabDefinition('result', 'race.results', 'fa-ranking-star');
        RoundType.Name = 'RoundType';
        RoundType.FieldName = 'RoundType';
        RoundType.DefinedFor = 'Both';
        RoundType.TranslationKey = 'race.roundtype';
        RoundType.Editable = true;
        RoundType.Filterable = true;
        RoundType.Sortable = true;
        RoundType.CssClasses = '';
        RoundType.value = (race) => race.RoundType;
        RoundType.setValue = (race, value) => (race.RoundType = value);
        RoundType.DetailEditor = markRaw(InputSelect);
        RoundType.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: RoundTypesWithUseEventSource } };
        RoundType.CellEditor = markRaw(InputSelect);
        RoundType.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: RoundTypesWithUseEventSource },
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        RoundType.CellRenderer = markRaw(DataSourceLookup);
        RoundType.CellRendererProps = { datasource: { mapsource: RoundTypesWithUseEventSource } };
        RoundType.ExportValueFormatter = (obj: Race) => {
            return RoundTypesWithUseEventSource.value.get(obj.RoundType)?.text;
        };
        RoundType.Validator = null;
        RoundType.SortDirection = undefined;
        RoundType.cellClassRules = undefined;
        RoundType.MinWidth = undefined;
        RoundType.MaxWidth = undefined;
        RoundType.Tooltip = null;
        this.Fields.push(RoundType);

        const HideFromLive = new FieldDef<Race>();
        HideFromLive.Tab = new TabDefinition('result', 'race.results', 'fa-ranking-star');
        HideFromLive.Name = 'HideFromLive';
        HideFromLive.FieldName = 'HideFromLive';
        HideFromLive.DefinedFor = 'Both';
        HideFromLive.TranslationKey = 'race.hidefromlive';
        HideFromLive.Editable = true;
        HideFromLive.Filterable = false;
        HideFromLive.Sortable = true;
        HideFromLive.CssClasses = '';
        HideFromLive.value = (race) => race.HideFromLive;
        HideFromLive.setValue = (race, value) => (race.HideFromLive = value);
        HideFromLive.DetailEditor = markRaw(InputBool);
        HideFromLive.DetailEditorProps = { label: 'race.hidefromlive' };
        HideFromLive.CellEditor = markRaw(InputBool);
        HideFromLive.CellEditorProps = {};
        HideFromLive.CellRenderer = markRaw(DisplayBool);
        HideFromLive.CellRendererProps = {};
        HideFromLive.Validator = null;
        HideFromLive.SortDirection = undefined;
        HideFromLive.cellClassRules = undefined;
        HideFromLive.MinWidth = undefined;
        HideFromLive.MaxWidth = undefined;
        HideFromLive.Tooltip = null;
        this.Fields.push(HideFromLive);

        const DigitsInBib = new FieldDef<Race>();
        DigitsInBib.Tab = new TabDefinition('result', 'race.results', 'fa-ranking-star');
        DigitsInBib.Name = 'DigitsInBib';
        DigitsInBib.FieldName = 'DigitsInBib';
        DigitsInBib.DefinedFor = 'Both';
        DigitsInBib.TranslationKey = 'race.digitsinbib';
        DigitsInBib.Editable = true;
        DigitsInBib.Filterable = false;
        DigitsInBib.Sortable = true;
        DigitsInBib.CssClasses = '';
        DigitsInBib.value = (race) => race.DigitsInBib;
        DigitsInBib.setValue = (race, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            race.DigitsInBib = value;
        };
        DigitsInBib.DetailEditor = markRaw(InputNullableInt);
        DigitsInBib.DetailEditorProps = {};
        DigitsInBib.CellEditor = markRaw(InputNullableInt);
        DigitsInBib.CellEditorProps = {};
        DigitsInBib.CellRenderer = markRaw(ValidatedInt);
        DigitsInBib.CellRendererProps = {};
        DigitsInBib.Validator = null;
        DigitsInBib.SortDirection = undefined;
        DigitsInBib.cellClassRules = undefined;
        DigitsInBib.MinWidth = undefined;
        DigitsInBib.MaxWidth = undefined;
        DigitsInBib.Tooltip = null;
        this.Fields.push(DigitsInBib);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Federations']);
        await useEventCommonStore().EnsureLoaded(['Races']);
        await useCommonStore().EnsureLoaded(['DecimalPlacesInResultsWithUseEvent']);
        await useCommonStore().EnsureLoaded(['RankingResolutionsWithUseEvent']);
        await useCommonStore().EnsureLoaded(['RoundTypesWithUseEvent']);
    }
}
